'use client'

import { useState } from 'react'
import { Stack, IconButton, Drawer, useTheme, useMediaQuery } from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import { usePathname } from 'next/navigation'
import { useLabels } from '@/utils/i18n'
import { MenuWrapper, MenuPaper } from './Menu.styles'
import { MenuLogo } from './MenuLogo'
import { NavButton } from './NavButton'
import { RefObject } from 'react'

interface MenuItem {
  key: string;
  href: string;
}

interface MenuProps {
  servicesRef: RefObject<HTMLHeadingElement>
  industriesRef: RefObject<HTMLHeadingElement>
  aboutRef: RefObject<HTMLHeadingElement>
}

const menuItems: MenuItem[] = [
  { key: 'industries', href: '/#industries' },
  { key: 'services', href: '/#services' },
  { key: 'about', href: '/#about' },
]

export function Menu({ servicesRef, industriesRef, aboutRef }: MenuProps) {
  const [mobileOpen, setMobileOpen] = useState(false)
  const pathname = usePathname()
  const labels = useLabels()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  const sectionRefs: Record<string, RefObject<HTMLHeadingElement>> = {
    services: servicesRef,
    industries: industriesRef,
    about: aboutRef,
  }

  const handleNavClick = (href: string) => {
    if (isMobile) {
      setMobileOpen(false)
    }
    if (href.includes('#')) {
      const hash = href.split('#')[1]
      const ref = sectionRefs[hash]
      if (ref && ref.current) {
        const appBarHeight = 64
        const paperPadding = window.innerWidth >= 1200 ? 48 : 0
        const totalOffset = appBarHeight + paperPadding
        ref.current.style.scrollMarginTop = `${totalOffset}px`
        ref.current.scrollIntoView({ behavior: 'smooth' })
        setTimeout(() => {
          ref.current && (ref.current.style.scrollMarginTop = '')
        }, 1000)
      }
    }
  }

  const menuContent = (
    <Stack 
      direction={isMobile ? 'column' : 'row'} 
      spacing={isMobile ? 2 : 4} 
      sx={{ 
        flexGrow: 1, 
        alignItems: 'center',
        justifyContent: isMobile ? 'flex-start' : 'flex-start',
        ml: isMobile ? 0 : 4
      }}
    >
      {menuItems.map((item) => (
        <NavButton
          key={item.href}
          component="a"
          href={item.href}
          color={pathname === item.href ? 'primary' : 'inherit'}
          active={pathname === item.href}
          onClick={(e) => {
            e.preventDefault()
            handleNavClick(item.href)
          }}
          fullWidth={isMobile}
        >
          {labels.menu[item.key as keyof typeof labels.menu]}
        </NavButton>
      ))}
    </Stack>
  )

  return (
    <MenuWrapper data-testid="menu">
      <MenuPaper>
        <Stack 
          direction="row" 
          spacing={2} 
          sx={{ 
            flexGrow: 1, 
            alignItems: 'center',
            justifyContent: isMobile ? 'space-between' : 'flex-start'
          }}
        >
          {isMobile ? (
            <>
              <MenuLogo />
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
              >
                <MenuIcon />
              </IconButton>
            </>
          ) : (
            <>
              <MenuLogo />
              {menuContent}
            </>
          )}
        </Stack>
      </MenuPaper>

      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 280,
            padding: 2,
          },
        }}
      >
        {menuContent}
      </Drawer>
    </MenuWrapper>
  )
} 